import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { ScheduledReport, ScheduleFrequency, ReportFormat } from '../../types/reports.types';
import { useScheduleReport, useUpdateSchedule } from '../../hooks/useReportsData';

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  report?: ScheduledReport;
}

export const ScheduleModal: React.FC<ScheduleModalProps> = ({ isOpen, onClose, report }) => {
  const { mutate: createSchedule, isPending: isCreating } = useScheduleReport();
  const { mutate: updateSchedule, isPending: isUpdating } = useUpdateSchedule();
  
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState<ScheduleFrequency>('weekly');
  const [time, setTime] = useState('09:00');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [emails, setEmails] = useState<string[]>(['']);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<'email' | 'webhook'>('email');
  const [isActive, setIsActive] = useState(true);
  
  useEffect(() => {
    if (report) {
      setName(report.name);
      setFrequency(report.schedule.frequency);
      setTime(report.schedule.time);
      setDayOfWeek(report.schedule.dayOfWeek || 1);
      setDayOfMonth(report.schedule.dayOfMonth || 1);
      setDeliveryMethod(report.delivery.method);
      setEmails(report.delivery.email || ['']);
      setWebhookUrl(report.delivery.webhookUrl || '');
      setIsActive(report.isActive);
    }
  }, [report]);
  
  const handleAddEmail = () => {
    setEmails([...emails, '']);
  };
  
  const handleRemoveEmail = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
  };
  
  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };
  
  const handleSubmit = () => {
    const data = {
      name,
      schedule: {
        frequency,
        time,
        ...(frequency === 'weekly' && { dayOfWeek }),
        ...(frequency === 'monthly' && { dayOfMonth }),
      },
      delivery: {
        method: deliveryMethod,
        ...(deliveryMethod === 'email' && { email: emails.filter(e => e) }),
        ...(deliveryMethod === 'webhook' && { webhookUrl }),
      },
      isActive,
    };
    
    if (report) {
      updateSchedule({ id: report.id, data }, { onSuccess: onClose });
    } else {
      createSchedule(data, { onSuccess: onClose });
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {report ? 'Edit Schedule' : 'Schedule Report'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Report Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Weekly Performance Report"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Frequency</label>
            <div className="flex gap-4">
              {(['daily', 'weekly', 'monthly'] as ScheduleFrequency[]).map(f => (
                <label key={f} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="frequency"
                    checked={frequency === f}
                    onChange={() => setFrequency(f)}
                    className="w-4 h-4"
                  />
                  <span className="capitalize">{f}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Time</label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
            />
          </div>
          
          {frequency === 'weekly' && (
            <div>
              <label className="block text-sm font-medium mb-2">Day of Week</label>
              <select
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
              >
                <option value={0}>Sunday</option>
                <option value={1}>Monday</option>
                <option value={2}>Tuesday</option>
                <option value={3}>Wednesday</option>
                <option value={4}>Thursday</option>
                <option value={5}>Friday</option>
                <option value={6}>Saturday</option>
              </select>
            </div>
          )}
          
          {frequency === 'monthly' && (
            <div>
              <label className="block text-sm font-medium mb-2">Day of Month</label>
              <select
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-2">Delivery Method</label>
            <div className="flex gap-4 mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="delivery"
                  checked={deliveryMethod === 'email'}
                  onChange={() => setDeliveryMethod('email')}
                  className="w-4 h-4"
                />
                <span>Email</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="delivery"
                  checked={deliveryMethod === 'webhook'}
                  onChange={() => setDeliveryMethod('webhook')}
                  className="w-4 h-4"
                />
                <span>Webhook</span>
              </label>
            </div>
            
            {deliveryMethod === 'email' ? (
              <div className="space-y-2">
                {emails.map((email, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      placeholder="email@example.com"
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                    />
                    {emails.length > 1 && (
                      <button
                        onClick={() => handleRemoveEmail(index)}
                        className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={handleAddEmail}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  + Add Email
                </button>
              </div>
            ) : (
              <input
                type="url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://api.example.com/webhook"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
              />
            )}
          </div>
          
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium">Enable Schedule</span>
            </label>
          </div>
        </div>
        
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isCreating || isUpdating || !name}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isCreating || isUpdating ? 'Saving...' : report ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};
